/// <reference types="paypal-checkout-components" />

import { client, paypalCheckout, type PayPalCheckout } from 'braintree-web';
import type { OnPaypalSubmit, ApiService, ServiceConfig } from './types.js';
import { PAYPAL_ERROR_TYPES } from './config.js';
import type { PaypalHooks } from './ports.js';

class PaypalService {
	private payPalCheckoutInstance: PayPalCheckout | null = null;
	private isReady = false;
	private initializedProductUuid: string | null = null;
	private initPromise: Promise<void> | null = null;
	private _onPageShow: ((event: PageTransitionEvent) => void) | null = null;

	constructor(
		private readonly apiService: ApiService,
		private readonly hooks: PaypalHooks,
		private readonly config: ServiceConfig
	) {}

	/**
	 * Prepares an SDK instance for the product experience that is active right now.
	 *
	 * The client token comes from Payment/PreProcess, which mints it against the
	 * active AoFL product. Each product maps to a different Braintree merchant, so
	 * a nonce produced by an SDK that was initialized under a different product is
	 * declined at billing. The instance is therefore keyed by product UUID and
	 * rebuilt whenever that key no longer matches.
	 * @param currencyCode - Currency the client token and PayPal SDK are loaded for.
	 * @returns {Promise<void>}
	 */
	async initializeSDK(currencyCode: string = 'USD'): Promise<void> {
		if (typeof window === 'undefined') {
			this.hooks.onError?.({
				type: PAYPAL_ERROR_TYPES.WINDOW_UNAVAILABLE,
				error: new Error('PayPal: Window unavailable'),
			});
			return;
		}

		const pending = this.initializeAfter(this.initPromise, currencyCode);
		this.initPromise = pending;

		try {
			await pending;
		} finally {
			if (this.initPromise === pending) {
				this.initPromise = null;
			}
		}
	}

	private async initializeAfter(
		previousInitialization: Promise<void> | null,
		currencyCode: string
	): Promise<void> {
		if (previousInitialization) {
			await previousInitialization;
		}

		const productUuid = this.config.getAoflProductUuid();
		if (!productUuid) {
			this.hooks.onError?.({
				type: PAYPAL_ERROR_TYPES.SDK_INIT,
				error: new Error('PayPal: AoFL product UUID unavailable'),
			});
			return;
		}

		if (this.isReady && this.initializedProductUuid === productUuid) return;

		this.isReady = false;
		this.payPalCheckoutInstance = null;
		await this.setupPayPalSDK(productUuid, currencyCode);
	}

	private async setupPayPalSDK(productUuid: string, currencyCode: string): Promise<void> {
		try {
			const token = await this.apiService.paymentPreProcess(
				productUuid,
				currencyCode,
				'paypal',
				{}
			);
			const clientInstance = await client.create({
				authorization: token,
			});

			this.payPalCheckoutInstance = await paypalCheckout.create({
				client: clientInstance,
			});

			await this.payPalCheckoutInstance.loadPayPalSDK({
				components: 'buttons',
				currency: currencyCode,
				'disable-funding': 'card,credit,paylater,venmo',
				vault: true,
			});

			if (typeof window.paypal === 'undefined') {
				throw new Error('PayPal SDK failed to load');
			}

			this.initializedProductUuid = productUuid;
			this.isReady = true;
		} catch (err) {
			this.isReady = false;
			this.initializedProductUuid = null;
			this.hooks.onError?.({ type: PAYPAL_ERROR_TYPES.SDK_INIT, error: err });
		}
	}

	async renderButton(
		onPaypalSubmit: OnPaypalSubmit,
		selector: string = '#paypal-button'
	): Promise<void> {
		this._registerPageShowHandler(onPaypalSubmit, selector);
		await this.initializeSDK();

		if (!this.isReady) {
			this.hooks.onError?.({ type: PAYPAL_ERROR_TYPES.RENDER_BEFORE_READY });
			return;
		}

		try {
			const buttons = window.paypal!.Buttons({
				fundingSource: window.paypal.FUNDING.PAYPAL.toString(),
				style: {
					height: 54,
				},
				createBillingAgreement: () => {
					// PayPal's createBillingAgreement must be synchronous (returns the
					// create-payment promise to the SDK), so this fires as a
					// fire-and-forget side effect rather than blocking the flow.
					this.hooks.onCheckoutInitiated?.();

					return this.payPalCheckoutInstance!.createPayment({
						flow: 'vault' as paypal.FlowType,
					});
				},
				// @ts-expect-error Callback doesn't need to return a value
				onApprove: async (data: AuthorizationData) => {
					try {
						const payload = await this.payPalCheckoutInstance!.tokenizePayment(data);

						this.hooks.onPaymentApproved?.();

						// TODO: remove the getDeviceId() fallback after the 'null' deviceID discrepancy is resolved.
						onPaypalSubmit({
							paypalType: 'vault',
							paypalNonce: payload.nonce,
							paypalDeviceData: this.config.getDeviceId() ?? 'unresolved_device_id_paypal',
						});
					} catch (err) {
						const error = err instanceof Error ? err : undefined;
						this.hooks.onError?.({
							type: PAYPAL_ERROR_TYPES.APPROVE,
							error: err,
							errorMessage: error?.message,
							errorName: error?.name,
							errorStack: error?.stack,
							hasPayPalCheckoutInstance: Boolean(this.payPalCheckoutInstance),
							hasTokenizePayment:
								typeof this.payPalCheckoutInstance?.tokenizePayment === 'function',
							onApproveData: data,
						});
						console.error('Error in PayPal onApprove', err); // eslint-disable-line
					}
				},
				onCancel: (data: unknown) => {
					this.hooks.onPaymentCancelled?.(data);
					console.log('PayPal payment canceled', JSON.stringify(data, null, 2));
				},
				onError: (err: unknown) => {
					this.hooks.onError?.({ type: PAYPAL_ERROR_TYPES.BUTTON, error: err });
					console.error('PayPal error', err);
				},
				onInit: () => {
					this.hooks.onButtonInitiated?.();
				},
			});

			const container = document.querySelector(selector);
			if (container) {
				buttons.render(selector);
			}
		} catch (err) {
			console.log('Error rendering PayPal button', err);
			this.hooks.onError?.({ type: PAYPAL_ERROR_TYPES.RENDER, error: err });
		}
	}

	private _registerPageShowHandler(onPaypalSubmit: OnPaypalSubmit, selector: string): void {
		if (this._onPageShow) {
			window.removeEventListener('pageshow', this._onPageShow);
		}

		this._onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				this.renderButton(onPaypalSubmit, selector);
			}
		};

		window.addEventListener('pageshow', this._onPageShow);
	}

	destroy(): void {
		if (this._onPageShow) {
			window.removeEventListener('pageshow', this._onPageShow);
			this._onPageShow = null;
		}
	}
}

export { PaypalService };
